"use client";

import type { InsightsTaskList, InsightsWorkingFrame } from "../types";
import type { InsightsCoverageSnapshot } from "./coverage";
import { InsightsCoverageStrip } from "./InsightsCoverageStrip";
import { RailReveal } from "./InsightsRailReveal";
import { InsightsTaskListPanel } from "./InsightsTaskList";
import { InsightsWorkingFramePanel } from "./InsightsWorkingFrame";

/**
 * Right-rail container: Coverage → Tasks → Working frame as equal sections.
 * Callers: InsightsChat (desktop rail + mobile dialog body).
 *
 * On first content, one top→bottom soft wipe covers the whole rail.
 */
export function InsightsInvestigationSidebar({
  coverage,
  workingFrame,
  taskList,
  hasCoverageBlockers,
  onTaskClick,
  showTasks = true,
  runLive = true,
}: {
  coverage: InsightsCoverageSnapshot | null;
  workingFrame: InsightsWorkingFrame | null;
  taskList: InsightsTaskList | null;
  hasCoverageBlockers?: boolean;
  onTaskClick?: (taskId: string) => void;
  /** Desktop shows tasks; mobile sheet can omit them. */
  showTasks?: boolean;
  /** False when the investigation run has ended. */
  runLive?: boolean;
}) {
  const hasCoverage = Boolean(coverage?.platforms.length);
  const hasFrame = Boolean(workingFrame?.definition?.trim());
  const hasTasks = Boolean(showTasks && taskList?.tasks?.length);
  const hasAny = hasCoverage || hasTasks || hasFrame;

  if (!hasAny) {
    return null;
  }

  return (
    <aside className="flex h-full min-h-0 w-full flex-col overflow-y-auto rounded-2xl border border-border/70 bg-background/80 p-4">
      <RailReveal active={hasAny}>
        <div className="flex flex-col gap-5">
          {hasCoverage ? <InsightsCoverageStrip coverage={coverage} layout="rail" /> : null}
          {hasTasks ? (
            <InsightsTaskListPanel
              taskList={taskList}
              hasCoverageBlockers={hasCoverageBlockers}
              onTaskClick={onTaskClick}
              variant="plain"
              runLive={runLive}
            />
          ) : null}
          {hasFrame ? <InsightsWorkingFramePanel frame={workingFrame} /> : null}
        </div>
      </RailReveal>
    </aside>
  );
}
